import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Search, PenSquare, Eye, MapPin, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  blogsAPI,
  resolveImageUrl,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  getCurrentAuthorRole,
} from '../services/blogsApi';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (_e) {
    return '';
  }
};

const BlogCard = ({ blog }) => {
  const cat = blog.category;
  return (
    <Link
      to={`/blog/${blog.slug || blog.id}`}
      className="group block"
      data-testid={`blog-card-${blog.slug || blog.id}`}
    >
      <Card className="overflow-hidden h-full transition-all group-hover:shadow-lg group-hover:-translate-y-0.5 duration-200">
        {blog.cover_image ? (
          <div className="aspect-[16/10] overflow-hidden bg-gray-100">
            <img
              src={resolveImageUrl(blog.cover_image)}
              alt={blog.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => {
                e.target.src =
                  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1200';
              }}
            />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-gradient-to-br from-emerald-50 to-emerald-100" />
        )}
        <CardContent className="p-4">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {cat && (
              <span
                className={`inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full ${
                  CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {CATEGORY_LABELS[cat] || cat}
              </span>
            )}
            {blog.destination && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                <MapPin className="h-3 w-3" /> {blog.destination}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-base leading-snug line-clamp-2 group-hover:text-[#7CB342] transition-colors">
            {blog.title}
          </h3>
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{blog.excerpt}</p>
          <div className="flex items-center justify-between mt-3 text-[11px] text-gray-500">
            <span className="truncate">
              By <span className="font-medium">{blog.author?.name || 'GeoStays'}</span>
              {blog.author?.role === 'admin' && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                  Team
                </span>
              )}
            </span>
            <span className="flex items-center gap-3 flex-shrink-0">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {formatDate(blog.published_at || blog.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" /> {blog.views || 0}
              </span>
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const BlogListPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState(params.get('search') || '');

  const category = params.get('category') || '';
  const search = params.get('search') || '';
  const role = getCurrentAuthorRole();

  const fetchBlogs = useMemo(
    () => async () => {
      try {
        setLoading(true);
        const [list, cats, featuredRes] = await Promise.all([
          blogsAPI.list({
            category: category || undefined,
            search: search || undefined,
            exclude_featured: !category && !search, // only exclude in the default view
            limit: 30,
          }),
          blogsAPI.categories(),
          !category && !search ? blogsAPI.featured() : Promise.resolve({ data: null }),
        ]);
        setBlogs(list.data || []);
        setCategories(cats.data || []);
        setFeatured(featuredRes.data || null);
      } catch (e) {
        toast.error('Failed to load blogs');
      } finally {
        setLoading(false);
      }
    },
    [category, search],
  );

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set('category', slug);
    else next.delete('category');
    setParams(next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput.trim()) next.set('search', searchInput.trim());
    else next.delete('search');
    setParams(next);
  };

  const handleWrite = () => {
    if (!role) {
      toast.info('Please sign in to write a blog');
      navigate('/login', { state: { from: '/blog/new' } });
      return;
    }
    navigate('/blog/new');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">
                Travel Stories from GeoStays
              </h1>
              <p className="text-gray-600 mt-3 text-base sm:text-lg">
                Destination guides, homestay stories, food journeys and travel tips — from our team
                and community of travellers across India.
              </p>
            </div>
            <div className="flex-shrink-0 flex gap-2">
              {role && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/blog/mine')}
                  data-testid="my-blogs-link"
                >
                  My Blogs
                </Button>
              )}
              <Button
                onClick={handleWrite}
                className="bg-[#7CB342] hover:bg-[#6da036]"
                data-testid="write-blog-btn"
              >
                <PenSquare className="h-4 w-4 mr-2" />
                Write a Blog
              </Button>
            </div>
          </div>

          {/* Search + categories */}
          <form onSubmit={handleSearch} className="mt-6 flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search stories, destinations, tips…"
                className="pl-9 bg-white"
                data-testid="blog-search-input"
              />
            </div>
            <Button type="submit" variant="outline" data-testid="blog-search-submit">
              Search
            </Button>
          </form>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="blog-categories">
            <button
              onClick={() => setCategory('')}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                !category
                  ? 'bg-[#7CB342] text-white border-[#7CB342]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
              data-testid="category-all"
            >
              All Stories
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCategory(c.slug)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  category === c.slug
                    ? 'bg-[#7CB342] text-white border-[#7CB342]'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`category-${c.slug}`}
              >
                {c.label} <span className="opacity-60">({c.count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-6xl">
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-[#7CB342] mx-auto" />
          </div>
        ) : (
          <>
            {featured && (
              <Link
                to={`/blog/${featured.slug || featured.id}`}
                className="group block mb-10"
                data-testid="featured-blog-card"
              >
                <Card className="overflow-hidden border-2 border-emerald-100 transition-all hover:shadow-xl">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="aspect-[16/10] md:aspect-auto overflow-hidden bg-gray-100">
                      <img
                        src={resolveImageUrl(featured.cover_image)}
                        alt={featured.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1600';
                        }}
                      />
                    </div>
                    <div className="p-6 sm:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-500 text-white">
                          <span>★</span> Editor&apos;s Pick
                        </span>
                        {featured.category && (
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                            CATEGORY_COLORS[featured.category] || 'bg-gray-100 text-gray-700'
                          }`}>
                            {CATEGORY_LABELS[featured.category] || featured.category}
                          </span>
                        )}
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 group-hover:text-[#7CB342] transition-colors leading-tight">
                        {featured.title}
                      </h2>
                      <p className="text-gray-600 mt-3 line-clamp-3">{featured.excerpt}</p>
                      <div className="flex items-center gap-3 mt-5 text-xs text-gray-500">
                        <span>By <span className="font-medium">{featured.author?.name || 'GeoStays'}</span></span>
                        {featured.destination && (
                          <>
                            <span>·</span>
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {featured.destination}</span>
                          </>
                        )}
                        <span>·</span>
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {featured.views || 0}</span>
                      </div>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#7CB342] group-hover:gap-3 transition-all">
                        Read the story →
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            )}

            {blogs.length === 0 ? (
              <div className="text-center py-16 text-gray-500" data-testid="empty-blogs">
                <PenSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-lg font-medium text-gray-700">No stories here yet</p>
                <p className="text-sm mt-1">Be the first to share your travel experience.</p>
                <Button
                  onClick={handleWrite}
                  className="mt-4 bg-[#7CB342] hover:bg-[#6da036]"
                >
                  <PenSquare className="h-4 w-4 mr-2" /> Write the first blog
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                {blogs.map((b) => (
                  <BlogCard key={b.id} blog={b} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogListPage;
